import {BaseElementAsync, MsgLabel, OSkinableInit} from "back/commons/basis";
import {IReg, REG} from "lib/commons/registry";
import {IDepotResUiEnv} from "lib/store/depot";
import {DOMSH} from "lib/commons/xml/domsh";
import {FORMS} from "lib/commons/forms";
import {Button} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {JSX} from "lib/commons/xml/dom";
import {IResFormCtx, IResFormIdentCtx, JCidMetas, JPersistMetas, OResFormIdentConfig, resPath} from "lib/store/res";
import {ICloseable} from "back/commons/widgets/popupable";


/**
 * Dialogue d'édition des metas d'une ressource.
 * Ce dialogue n'exécute pas le push cid mais retourne les paramètres Cid.
 * Ce dialogue fournit en retour un JCidMetas à envoyer.
 */
export interface EditMetasRes extends BaseElementAsync {
	initialize(init: OEditMetasResInit): this
}

export interface OEditMetasResInit extends OSkinableInit {
	reg?: IReg<IDepotResUiEnv>;

	/**
	 * A configurer pour éditer les metas identificatoires de la ressource (déplacement, renommage...).
	 */
	formIdent?: OResFormIdentConfig

	/** Key pour IResType.resForm(). Par défaut : "editMetas" ou "editIdent" si formIdent est renseigné. */
	formView?: string

	/**
	 * Valeurs par défaut de 'reg.env.nodeInfos.metas' passé au formulaire.
	 *
	 * Algo:
	 * Si defaultValue est un objet :
	 * 	const datas = Object.assign(Object.create(defaultValues), reg.env.nodeInfos?.metas);
	 *
	 * Si defaultValue est une fonction :
	 * 	const datas = Object.assign(defaultValues(this), reg.env.nodeInfos?.metas);
	 *
	 * Si defaultValue == null
	 * 	const datas = {};
	 */
	defaultValues?: JCidMetas | ((editMetas: EditMetasRes) => JCidMetas)

	/**
	 * Valeurs imposées / modifiées sur la res à éditer (écrasant les 'reg.env.nodeInfos.metas' courants).
	 *
	 * Algo:
	 * `datas` est le résultat tel que définit par OEditMetasResInit.defaultValues
	 *
	 * Si overrideValues est un objet :
	 * 	Object.assign(datas, overrideValues)
	 *
	 * Si overrideValues est une fonction :
	 * 	overrideValues(this, datas)
	 */
	overrideValues?: JCidMetas | ((editMetas: EditMetasRes, values: JCidMetas) => JCidMetas)

	/**
	 * Personnalisation du libellé du bouton de validation. Par défaut: "Enregistrer" ou "Renommer" si formIdent est renseigné.
	 */
	doBtnLabel?: string

	/** Contexte d'appel auprès duquel retourner le résultat. */
	holder?: ICloseable<JCidMetas | null>
}

export class EditMetasRes extends BaseElementAsync {
	reg: IReg<IDepotResUiEnv>;
	config: OEditMetasResInit;

	protected formEditElt: HTMLFormElement;
	protected doBtn: Button;
	protected msgArea: MsgLabel;

	get holder(): ICloseable<JCidMetas | null> {return this.config.holder || POPUP.findPopupableParent(this)}

	protected async _initialize(init: OEditMetasResInit): Promise<void> {
		this.config = init;
		this.reg = init.reg;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("form-control-areas", sr);
		this.reg.installSkin("standard-dialog", sr);
		this._initAndInstallSkin(this.localName, init);

		//Attente fin de chargement
		while (this.reg.env.nodeInfosPending) await this.reg.env.nodeInfosPending;
		//TODO écoute this.reg.env.nodeInfosChange et notif + refresh...

		//Construction des datas avec ses overrides avant exploitation de this.reg.env.resType (si prc changé)
		let datas: JPersistMetas = this.reg.env.nodeInfos?.metas;

		if (typeof init.defaultValues === "function") datas = Object.assign(init.defaultValues(this), datas)
		else if (init.defaultValues != null) datas = Object.assign(Object.create(init.defaultValues), datas)
		else if (datas == null) datas = Object.create(null);

		if (typeof init.overrideValues === "function") init.overrideValues(this, datas)
		else if (init.overrideValues != null) Object.assign(datas, init.overrideValues);

		//Formulaire
		const area = this.reg.env.resType.resForm(init.formView || (init.formIdent ? "editIdent" : "editMetas"));

		const skinForm = area.getSkin(this.reg);
		if (skinForm) this.reg.installSkin(skinForm, sr);
		const skinFormOver = area.getSkinOver(this.reg);
		if (skinFormOver) this.reg.installSkin(skinFormOver, sr);

		let ctx: IResFormCtx & IResFormIdentCtx = Object.create(init.formIdent || null);
		ctx.reg = this.reg;
		ctx.buildControlLabel = true;

		if (init.formIdent) {
			ctx.callback = this;
			this.msgArea = <MsgLabel id="msg"/> as MsgLabel;
		}
		const fields = await area.loadBody(ctx);

		this.formEditElt = <form id="scroll">{fields}</form> as HTMLFormElement;
		this.formEditElt.onsubmit = (ev) => false;
		this.doBtn = <Button label={init.doBtnLabel || (init.formIdent ? "Renommer" : "Enregistrer")} class="default" onclick={this.onSave} ui-context="dialog"/> as Button;
		sr.append(
			this.formEditElt,
			<footer id="footer">
				{this.msgArea}
				{this.doBtn}
				<c-button label="Annuler" onclick={this.onCancel} ui-context="dialog"/>
			</footer>
		);

		FORMS.jsonToForm(datas, this.formEditElt);
		FORMS.findFirstFocusable(this.formEditElt)?.focus();
	}

	async onSave(this: Button, ev: Event) {
		const me = DOMSH.findHost<EditMetasRes>(this);
		if (await FORMS.checkAsyncValidity(me.formEditElt)) {
			const currentInfos = me.reg.env.nodeInfos;
			const result: JCidMetas = {
				path: me.reg.env.path,
				processing: me.reg.env.resType.prc || currentInfos?.prc
			}
			if (currentInfos.resId) result.olderResId = currentInfos.resId;
			if (me.config.formIdent) result.olderPath = me.reg.env.path; //renommage
			FORMS.formToJson(me.formEditElt, result);
			//console.log("onSave:::",result);
			me.holder.close(result);
		} else {
			FORMS.reportValidity(me.formEditElt);
		}
	}

	onCancel(this: Button, ev: Event) {
		DOMSH.findHost<EditMetasRes>(this).holder.close();
	}

	/** En cas d'édition de l'identité. */
	identState(resState: 'invalid' | 'pending' | 'exist' | 'free' | 'inTrash', targetPath?: resPath | null, msg?: string, msgLevel?: 'info' | 'error' | 'warning' | 'valid'): void {
		this.msgArea.setCustomMsg(msg, msgLevel);
		this.doBtn.disabled = resState === "invalid";
	}
}

REG.reg.registerSkin('store-editmetas-res', 1, /* language=CSS */ `
	#scroll {
		flex: 1;
		display: flex;
		min-height: 0;
		overflow: auto;
		flex-direction: column;
		width: 100%;
	}

	form {
		display: flex;
		flex: 3;
	}

	form > fieldset {
		border: none;
		padding: 0;
		margin: 0;
		display: grid;
		grid-template-columns: auto 1fr;
		grid-gap: .5rem .5rem;
		grid-auto-rows: max-content;
		padding: .5rem;
		width: 40em;
		max-width: calc(100% - 1rem);
		align-self: center;
	}

	.ctrlLbl {
		display: contents;
	}

	.lbl {
		justify-content: flex-end;
		word-break: keep-all;
		max-width: 15rem;
		white-space: unset;
	}

	.valueEntry {
		display: inline-block;
	}

	:any-link {
		color: unset;
	}

	#msg {
		flex: 1;
		margin: 0 .5em;
		justify-content: start;
	}
`);

customElements.define('store-editmetas-res', EditMetasRes);